import { lamportsToUiAmount, bsMath, percentUiToDecimals } from "@bridgesplit/utils";
import { LoanCreationParams } from "@bridgesplit/abf-sdk";
import { DEFAULT_SLIPPAGE } from "app/components/common";
import { SwapParams } from "@loopscale/pandora-types";
import {
    ExternalWindQuoteParams,
    getLoopSlippage,
    buildWindQuoteRequest,
    getLoopOraclesFromMap
} from "@bridgesplit/abf-react";

import { LoopWinder, LoopWinderCtx, WindQuoteTarget, WindQuoteParams } from "./types";

export class BaseWinder implements LoopWinder {
    name = "BaseWinder";
    windQuoteTarget: WindQuoteTarget = "pandora";

    constructor(protected isPrincipalWind: boolean = false) {}

    getLoanCreationParams(_: LoopWinderCtx) {
        return "Default" as LoanCreationParams;
    }

    // keeping this here - we used to use it for orca loop and would like to have a reference to the variable responses we need when building loop
    // ie I dont want to overloop this step in the future
    getPriceOverride(_ctx: LoopWinderCtx): number | undefined {
        return undefined;
    }

    getFormInputMint(ctx: LoopWinderCtx): string | undefined {
        const { form } = ctx;
        return form?.inputToken?.mint;
    }

    getCollateralInputAmountForQuote(ctx: LoopWinderCtx): number | undefined {
        return this.isPrincipalWind
            ? this.principalCollateralInputAmountForQuote(ctx)
            : this.collateralInputAmountForQuote(ctx);
    }

    getLeveragedCollateralAmountToDeposit(ctx: LoopWinderCtx): number | undefined {
        return this.isPrincipalWind
            ? this.principalLeveragedCollateralAmount(ctx)
            : this.collateralLeveragedCollateralAmount(ctx);
    }

    getCollateralInputAmount(ctx: LoopWinderCtx): number | undefined {
        return this.isPrincipalWind
            ? this.principalCollateralInputAmount(ctx)
            : this.collateralCollateralInputAmount(ctx);
    }

    calculateMarketCollateralAmounts(ctx: LoopWinderCtx) {
        return this.isPrincipalWind
            ? this.principalMarketCollateralAmounts(ctx)
            : this.collateralMarketCollateralAmounts(ctx);
    }

    buildWindQuoteParams(ctx: LoopWinderCtx): WindQuoteParams | undefined {
        const { loopExpanded, form, quote, userWallet, oraclePrices } = ctx;

        const { principalOracle } = getLoopOraclesFromMap(loopExpanded, oraclePrices);
        const defaultSlippageDecimals = percentUiToDecimals(getLoopSlippage(loopExpanded).defaultSlippageUi);

        const externalWindQuoteParams: ExternalWindQuoteParams = {
            loopExpanded: loopExpanded,
            principalAmount: quote?.principalAmount,
            slippagePercentDecimals: form?.percentSlippageDecimals ?? defaultSlippageDecimals,
            inputAmount: form?.inputAmount,
            clmmPosition: undefined,
            inputToken: form?.inputToken,
            windMint: loopExpanded?.principalToken.mint,
            userWallet,
            principalUsdPrice: principalOracle?.usdPrice
        };

        return buildWindQuoteRequest(externalWindQuoteParams);
    }

    buildPandoraPrincipalWindQuoteParams(ctx: LoopWinderCtx): SwapParams | undefined {
        if (!this.isPrincipalWind) return undefined;
        const { loopExpanded, form, userWallet, oraclePrices } = ctx;

        const { principalOracle } = getLoopOraclesFromMap(loopExpanded, oraclePrices);

        const defaultSlippageDecimals = percentUiToDecimals(getLoopSlippage(loopExpanded).defaultSlippageUi);
        const windMint = loopExpanded?.principalToken.mint;
        const externalWindQuoteParams: ExternalWindQuoteParams = {
            loopExpanded: loopExpanded,
            principalAmount: form?.inputAmount,
            slippagePercentDecimals: form?.percentSlippageDecimals ?? defaultSlippageDecimals,
            inputAmount: 0,
            clmmPosition: undefined,
            inputToken: form?.inputToken,
            windMint,
            userWallet,
            skip: !form?.multiplier,
            principalUsdPrice: principalOracle?.usdPrice
        };

        return buildWindQuoteRequest(externalWindQuoteParams);
    }

    // === Private: Collateral wind logic ===

    private collateralInputAmountForQuote(ctx: LoopWinderCtx): number | undefined {
        const { form } = ctx;
        return form?.inputAmount;
    }

    private collateralLeveragedCollateralAmount(ctx: LoopWinderCtx): number | undefined {
        const { loopExpanded, externalQuote, form } = ctx;
        if (!loopExpanded || !externalQuote || !form?.inputAmount || !form?.multiplier) return undefined;

        if (externalQuote.quote.minOutputAmount === 0) {
            return form.inputAmount * (form.multiplier - (form.percentSlippageDecimals ?? DEFAULT_SLIPPAGE));
        }

        return (
            form.inputAmount +
            lamportsToUiAmount(externalQuote.quote.minOutputAmount, loopExpanded.collateralToken.decimals)
        );
    }

    private collateralCollateralInputAmount(ctx: LoopWinderCtx): number | undefined {
        const { inputAmount } = ctx;
        return inputAmount;
    }

    private collateralMarketCollateralAmounts(ctx: LoopWinderCtx) {
        const { loopExpanded, externalQuote, quote } = ctx;

        const collateralInputAmount = quote?.collateralAmount;
        const collateralMinOutputAmount = externalQuote?.quote.minOutputAmount;
        const decimals = loopExpanded?.collateralToken.decimals;

        const swappedMinOutputCollateralAmount = lamportsToUiAmount(collateralMinOutputAmount, decimals);
        const totalMinOutputCollateralAmount = bsMath.add(swappedMinOutputCollateralAmount, collateralInputAmount) ?? 0;

        return { totalMinOutputCollateralAmount, swappedMinOutputCollateralAmount };
    }

    // === Private: Principal wind logic ===

    private principalCollateralInputAmountForQuote(ctx: LoopWinderCtx): number | undefined {
        const { loopExpanded, principalWindExternalQuote } = ctx;
        if (!principalWindExternalQuote || !loopExpanded) return undefined;
        const minCollateralAmount = principalWindExternalQuote.quote.minOutputAmount;
        const minCollateralAmountUi = lamportsToUiAmount(minCollateralAmount, loopExpanded.collateralToken.decimals);
        return minCollateralAmountUi;
    }

    private principalLeveragedCollateralAmount(ctx: LoopWinderCtx): number | undefined {
        const { loopExpanded, externalQuote, form } = ctx;
        if (!loopExpanded || !externalQuote || !form?.inputAmount || !form?.multiplier) return undefined;

        if (externalQuote.quote.minOutputAmount === 0) {
            return form.inputAmount * (form.multiplier - (form.percentSlippageDecimals ?? DEFAULT_SLIPPAGE));
        }

        return lamportsToUiAmount(externalQuote.quote.minOutputAmount, loopExpanded.collateralToken.decimals);
    }

    private principalCollateralInputAmount(ctx: LoopWinderCtx): number | undefined {
        const { inputAmount, oraclePrices, loopExpanded } = ctx;

        const { principalOracle, collateralOracle } = getLoopOraclesFromMap(loopExpanded, oraclePrices);

        const principalInputInCollateral = bsMath.div(
            bsMath.mul(inputAmount, principalOracle?.usdPrice ?? 0),
            collateralOracle?.usdPrice
        );
        return principalInputInCollateral;
    }

    private principalMarketCollateralAmounts(ctx: LoopWinderCtx) {
        const { loopExpanded, externalQuote } = ctx;

        const collateralMinOutputAmount = externalQuote?.quote.minOutputAmount;
        const collateralOutputMintMetadata = loopExpanded?.collateralToken;

        const swappedMinOutputCollateralAmount = lamportsToUiAmount(
            collateralMinOutputAmount,
            collateralOutputMintMetadata?.decimals
        );

        // do not add collateral input to a full principal wind
        return {
            totalMinOutputCollateralAmount: swappedMinOutputCollateralAmount,
            swappedMinOutputCollateralAmount
        };
    }
}
